<script setup>
import Icon from '@/Components/Icon/index.vue';
import Button from '@/Components/Button.vue';
import { computed } from 'vue';
import ListItem from '@/Components/List/Item/index.vue';
import { THEMES, EVENT } from '@/constants';
import { Link, usePage } from '@inertiajs/vue3';
import useAnalytics from '@/composables/useAnalytics';

const analytics = useAnalytics();

const props = defineProps({
  course: {
    type: Object,
    required: true,
  },
  tuitionBracket: {
    type: Object,
    required: false
  },
});

const page = usePage();
const context = computed(() => page.props?.context);
const theme = context.value.theme;
const enquiryEnabled = context.value.enquiry_enabled ?? false;

const courseSite = computed(() => {
  if(!props.course.sites.length){
    return null;
  }
  let sites = props.course.sites;

  if (context.value.search_filters?.all_scoped_states) {
    sites = sites.filter((site) => 
      context.value.search_filters.all_scoped_states.includes(site.state)
    );
  }

  if(props.tuitionBracket) {
    sites = sites.filter((site) => 
      site.fees.overseas_full_fee && 
      site.fees.overseas_full_fee > props.tuitionBracket.min && 
      site.fees.overseas_full_fee < props.tuitionBracket.max
    );
  }
  
  return sites[0];  
});

const courseSiteLocale = computed(() => {
  const { region, state, country } = courseSite.value ?? {};

  return region?.name || state?.toUpperCase() || country?.name || null;
});

const duration = computed(() => {
  if(courseSite.value && courseSite.value.attendance && courseSite.value.attendance.length){
    const length = courseSite.value.attendance[0]["length"];
    const period = courseSite.value.attendance[0].du_name;
    const mode = courseSite.value.attendance[0].name;
  
    return `${length} ${period} ${mode}`;
  }
  return null;
})

const auDollarFormatter = (dollars) => `AUD $${Intl.NumberFormat().format(dollars)}`;

const tuition = computed(() => {
  if(courseSite.value && courseSite.value.fees && courseSite.value.fees.overseas_full_fee) {
    return `${auDollarFormatter(courseSite.value.fees.overseas_full_fee)}`
  }
  return null;
})

const startDate = computed(() => {
  if(courseSite.value && courseSite.value.course_start_date) {
    return `${courseSite.value.course_start_date}`
  }
  return null;
});

const learnMoreBtnVariant = (theme) => {
  return {
    [THEMES.STUDY_AUS]: 'link',
    [THEMES.STUDY_MELB]: 'secondary',
    [THEMES.STUDY_QLD]: 'secondary',
  }[theme];
};

const learnMoreClick = (course) => {
  const fieldOfStudies = course.field_of_studies.map(item => item.name) ?? null;
  const careers = course.careers.map(item => item.name) ?? null;

  analytics.track(EVENT.ACIR_COURSE_PROFILE_LOADED, {
    provider: course.organisation.name,
    qualification: course.qualification.name,
    field_of_studies: fieldOfStudies,
    careers,
  });
};
</script>

<template>
        <ListItem class="course-list-card" :image-set="course.organisation.logos" :alt-text="course.organisation.name" fallback-icon="Book" :logo-link="`/provider/${course.organisation.slug}/${course.organisation.id}`">
          <template #title>
            <Link
                :href="`/course/${course.slug}/${course.id}`"
                class="border-none"
              >{{ course.name }}</Link>
          </template>
          <template #subtitle>
            <Link
            :href="`/provider/${course.organisation.slug}/${course.organisation.id}`"
            :aria-label="`Course provider ${course.organisation.name}`"
            class="border-none font-bold inline-block my-1"
          >{{ course.organisation.name }}</Link>
          </template>

          <template #content>
            <p
          v-if="courseSiteLocale"
          class="text-sm flex align-items-center mb-2"
        >
          <Icon
            class="me-2"
            path="University"
            :height="16"
            :width="16"
          />
          <a
            tabindex="-1"
            class="ms-2 text-sm"
            :href="`/course/${course.slug}/${course.id}`"
          >
            {{ courseSiteLocale }} 
            <template v-if="course.sites.length && course.sites.length > 1">
              + {{ `${course.sites.length - 1}  other ${course.sites.length > 2 ? 'campuses' : 'campus'}` }}
            </template>
          </a>
        </p>
       
        <p v-if="duration" class="text-sm mb-2 flex align-items-center"><Icon
            class="me-2 self-center" path="Clock" 
            :height="16"
            :width="16"/>Duration: {{ duration }}</p>
        <p v-if="tuition" class="text-sm mb-2 flex align-items-center"><Icon
            class="me-2 self-center" path="DollarSquare" 
            :height="16"
            :width="16"/>Estimated total course cost: {{ tuition }}</p>
        <p v-if="startDate" class="text-sm mb-2 flex align-items-center"><Icon
            class="me-2 self-center" path="Calendar" 
            :height="16"
            :width="16"/>Start date: {{ startDate }}</p>
          </template>

          <template v-if="enquiryEnabled" #action1>
            <Button
              :aria-label="`Enquire about ${course.name}`"
              type="link"
              :link="`/course/${course.slug}/${course.id}/enquiry`"
              class="w-full md:w-fit"
            >
              Enquire
            </Button>
          </template>
          <template #action2>
            <Button
              :variant="learnMoreBtnVariant(theme)"
              type="link"
              :aria-label="`Learn more about ${course.name}`"
              :link="`/course/${course.slug}/${course.id}`"
              class="w-full md:w-fit "
              @click="learnMoreClick(course)"
            >
              Learn More
            </Button>
          </template>
            
        </ListItem>
        
</template>
